import { FC, useCallback, useEffect, useState } from 'react'
import { Button, Tag, Tooltip } from 'antd'
import { CloseOutlined, CopyOutlined, FileTextOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { JsonCode } from '@/components/ai-elements/json-code'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { jingqueAdminApi } from '@/api/jingqueAdminApi'

/** doc 122:文件内容 Peek 浮卡 — 浮于对话区上层,连续点文件原位换内容。 */

interface PeekFile {
  path: string
  contextId: string
  runAs: string
  // doc 125-Fix2:文件树/Peek 跟随当前 tab 模板白名单;空 = 不限制
  templateId?: string
}

interface FileContent {
  content: string
  totalLines: number
}

const extOf = (p: string) => {
  const seg = p.split('/').pop() || ''
  const i = seg.lastIndexOf('.')
  return i >= 0 ? seg.slice(i + 1).toLowerCase() : ''
}

export const FilePeekCard: FC<{
  file: PeekFile
  onClose: () => void
}> = ({ file, onClose }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [data, setData] = useState<FileContent | null>(null)
  const [copied, setCopied] = useState<'content' | 'path' | ''>('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const r = await jingqueAdminApi.agentHarness.fsBrowse({
        action: 'read',
        path: file.path,
        context_id: file.contextId,
        run_as: file.runAs || undefined,
        template_id: file.templateId || undefined,
      })
      if (r.error) {
        setError(r.error.error || '读取失败')
        setData(null)
      } else {
        const d = r.envelope?.data || {}
        setData({ content: d.content ?? '', totalLines: d.total_lines ?? 0 })
      }
    } catch (e: any) {
      setError(e?.message || String(e))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [file.path, file.contextId, file.runAs, file.templateId])

  useEffect(() => {
    load()
  }, [load])

  // Esc 关闭
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const copy = async (what: 'content' | 'path') => {
    try {
      await navigator.clipboard.writeText(what === 'path' ? file.path : data?.content || '')
      setCopied(what)
      setTimeout(() => setCopied(''), 1500)
    } catch {
      /* clipboard 不可用静默 */
    }
  }

  const ext = extOf(file.path)
  const isJson = ext === 'json'
  const isMd = ext === 'md'

  return (
    <div
      className="absolute left-2 top-2 bottom-2 z-10 flex w-[420px] max-w-[calc(100%-16px)] origin-left flex-col overflow-hidden rounded-xl border bg-background shadow-xl animate-in fade-in zoom-in-95 duration-150"
      role="dialog"
      aria-label={`预览 ${file.path}`}
    >
      {/* 头部:类型图标 + 路径 + 操作 */}
      <div className="flex h-9 shrink-0 items-center gap-1 border-b px-2.5">
        <FileTextOutlined className="shrink-0 text-muted-foreground" style={{ fontSize: 13 }} />
        <span className="min-w-0 flex-1 truncate text-xs font-medium" title={file.path}>
          {file.path}
        </span>
        {ext && (
          <Tag className="mr-0 shrink-0" color={isJson ? 'blue' : isMd ? 'purple' : 'default'}>
            {ext}
          </Tag>
        )}
        <Tooltip title={copied === 'content' ? '已复制' : '复制内容'}>
          <Button
            type="text"
            size="small"
            disabled={!data}
            icon={<CopyOutlined style={{ fontSize: 12 }} className={copied === 'content' ? 'text-emerald-600' : ''} />}
            onClick={() => copy('content')}
          />
        </Tooltip>
        <Tooltip title={copied === 'path' ? '已复制' : '复制路径'}>
          <Button
            type="text"
            size="small"
            icon={<LinkOutlined style={{ fontSize: 12 }} className={copied === 'path' ? 'text-emerald-600' : ''} />}
            onClick={() => copy('path')}
          />
        </Tooltip>
        <Tooltip title="重新加载">
          <Button type="text" size="small" icon={<ReloadOutlined style={{ fontSize: 12 }} />} onClick={load} />
        </Tooltip>
        <Tooltip title="关闭 (Esc)">
          <Button type="text" size="small" icon={<CloseOutlined style={{ fontSize: 12 }} />} onClick={onClose} />
        </Tooltip>
      </div>

      {/* 体:按类型渲染 */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading && <Shimmer>读取文件…</Shimmer>}
        {!loading && error && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
            <span className="text-destructive">{error}</span>
            <Button size="small" onClick={load}>重试</Button>
          </div>
        )}
        {!loading && !error && data && (
          <>
            {isJson ? (
              <JsonCode code={prettyJson(data.content)} maxHeight={1e9} />
            ) : isMd ? (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content}</ReactMarkdown>
              </div>
            ) : (
              <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-relaxed">{data.content}</pre>
            )}
          </>
        )}
      </div>

      {/* 底部 meta */}
      {!loading && data && (
        <div className="flex h-6 shrink-0 items-center border-t px-2.5 text-[10px] text-muted-foreground">
          {data.totalLines} 行
        </div>
      )}
    </div>
  )
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
